// orderSlice.js (WEB VERSION)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Baseurl } from "../../config/BaseUrl";

// ======================= API CALLS =======================

// Create Order
export const createOrder = createAsyncThunk(
  "order/createOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${Baseurl}/api/v1/ordershop/new`,
        orderData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Network Error");
    }
  }
);

// Get All Orders for Client
export const getOrdersByClientId = createAsyncThunk(
  "order/getOrdersByClientId",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${Baseurl}/api/v1/ordershop/orderbyclientid/${clientId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Network Error");
    }
  }
);

// Get Last 10 Orders
export const getLast10Orders = createAsyncThunk(
  "order/getLast10Orders",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${Baseurl}/api/v1/ordershop/tenOrderbyclient/${clientId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Network Error");
    }
  }
);

// Get Single Order
export const getOrderById = createAsyncThunk(
  "order/getOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${Baseurl}/api/v1/ordershop/singleorderbyclient/${orderId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Network Error");
    }
  }
);

// Update Order Status
export const updateOrderStatus = createAsyncThunk(
  "order/updateOrderStatus",
  async ({ orderId, statusData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${Baseurl}/api/v1/ordershop/updateorder/${orderId}`,
        statusData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Network Error");
    }
  }
);

// ======================= INITIAL STATE =======================

const initialState = {
  OrderTotal: [],
  currentOrder: null,
  orderLoading: false,
  createLoading: false,
  updateLoading: false,

  error: null,
  success: false,
};

// ======================= SLICE =======================

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    Orderget(state, action) {
      state.OrderTotal = action.payload;
      state.orderLoading = false;
    },
    clearOrder(state) {
      state.OrderTotal = [];
      state.currentOrder = null;
    },
    clearCurrentorder(state) {
      state.currentOrder = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearSuccess(state) {
      state.success = false;
    },

    // Local update (no API call)
    updateOrderStatusLocal(state, action) {
      const { orderId, status, statusText } = action.payload;

      const orderIndex = state.OrderTotal.findIndex(
        (order) => order._id === orderId
      );

      if (orderIndex !== -1) {
        state.OrderTotal[orderIndex].Status = status;
        state.OrderTotal[orderIndex].StatusText = statusText;
      }

      if (state.currentOrder?._id === orderId) {
        state.currentOrder.Status = status;
        state.currentOrder.StatusText = statusText;
      }
    },
  },

  // ======================= EXTRA REDUCERS =======================
  extraReducers: (builder) => {
    builder
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.createLoading = false;
        state.success = true;

        if (action.payload.success && action.payload.orderShop) {
          state.OrderTotal = [
            action.payload.orderShop,
            ...state.OrderTotal,
          ];
          state.currentOrder = action.payload.orderShop;
        }
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.createLoading = false;
        state.error =
          action.payload?.message || "Failed to create order";
      })

      // Get Orders By Client
      .addCase(getOrdersByClientId.pending, (state) => {
        state.orderLoading = true;
        state.error = null;
      })
      .addCase(getOrdersByClientId.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.OrderTotal = action.payload.order || [];
        }
      })
      .addCase(getOrdersByClientId.rejected, (state, action) => {
        state.orderLoading = false;
        state.error =
          action.payload?.message || "Failed to fetch orders";
      })

      // Last 10 Orders
      .addCase(getLast10Orders.pending, (state) => {
        state.orderLoading = true;
      })
      .addCase(getLast10Orders.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.OrderTotal = action.payload.order || [];
        }
      })
      .addCase(getLast10Orders.rejected, (state) => {
        state.orderLoading = false;
      })

      // Single Order
      .addCase(getOrderById.pending, (state) => {
        state.orderLoading = true;
        state.error = null;
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.currentOrder = action.payload.order;
        }
      })
      .addCase(getOrderById.rejected, (state, action) => {
        state.orderLoading = false;
        state.error =
          action.payload?.message || "Failed to fetch order";
      })

      // Update Order
      .addCase(updateOrderStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.success = true;

        if (action.payload.success && action.payload.order) {
          const index = state.OrderTotal.findIndex(
            (order) => order._id === action.payload.order._id
          );
          if (index !== -1) {
            state.OrderTotal[index] = action.payload.order;
          }

          if (
            state.currentOrder?._id === action.payload.order._id
          ) {
            state.currentOrder = action.payload.order;
          }
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error =
          action.payload?.message || "Failed to update order";
      });
  },
});

// Export Actions
export const {
  Orderget,
  clearOrder,
  clearCurrentorder,
  clearError,
  clearSuccess,
  updateOrderStatusLocal,
} = orderSlice.actions;

// Reducer
export default orderSlice.reducer;
